import React, { useState } from "react";
import { DeleteDialog, Modal } from "../components";
import EditRow from "./editrow";
import TimetableRowCard from "./timetablerow";
import { sortRows } from "./timetablesort";
import Panel from "components/panels/panel";
import { useCleanTimetableCache, useDeleteTimetable, useTimetablesApi } from "./api";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import Choose from "./LoadFromShow/Choose";
import LoadFromShow from "./LoadFromShow/LoadFromShow";
import { ButtonSecondary } from "components/Atoms/Button";

interface EditRowState {
  showModal: boolean
  timetableRowId?: number
}

interface DeleteRowState {
  showDeleteDialog: boolean
  deleteRowId?: number
}

interface Props {
  showId: number
  panelConfig: server.dto.TimetablePanelConfig
}

const TimetablePanel = ({ showId, panelConfig }: Props) => {
  const [showEditModal, setShowEditModal] = useState<EditRowState>({ showModal: false, timetableRowId: undefined });
  const [deleteRowModal, setDeleteRowModal] = useState<DeleteRowState>({ showDeleteDialog: false, deleteRowId: undefined });
  const [displayChooseOptionModal, setDisplayChooseOptionModal] = useState<boolean>(false);
  const [displayLoadFromShowModal, setDisplayLoadFromShowModal] = useState<boolean>(false);

  const timetablesApi = useTimetablesApi(showId);
  const deleteTimetables = useDeleteTimetable();
  const cleanCache = useCleanTimetableCache();

  const deleteTimetableRow = async (id: number) => {
    setDeleteRowModal({ showDeleteDialog: true, deleteRowId: id });
  };

  const onEditRowClicked = rowId => {
    setShowEditModal({ showModal: true, timetableRowId: rowId });
  };

  const onNewRowClicked = () => {
    setShowEditModal({ showModal: true, timetableRowId: undefined });
  };

  const onDisplayChooseOptionModal = () => {
    setDisplayChooseOptionModal(true);
  };

  const onEditModalClose = () => {
    setShowEditModal({ showModal: false, timetableRowId: showEditModal.timetableRowId });
  };

  const onDeleteConfirm = async () => {
    await deleteTimetables.mutateAsync({ timetableEntryId: deleteRowModal.deleteRowId });
    closeDeleteModal();
  };

  const closeDeleteModal = () => {
    setDeleteRowModal({ showDeleteDialog: false, deleteRowId: undefined });
  };

  const onTimetableCopied = () => {
    cleanCache();
    setDisplayLoadFromShowModal(false);
  };

  const editedRow = showEditModal.timetableRowId ? timetablesApi.data.data.find(e => e.timetableEntryId === showEditModal.timetableRowId) : undefined;

  const noStartTimes = timetablesApi.data.data.every(e => !e.startTime);
  const noEndTimes = timetablesApi.data.data.every(e => !e.endTime);

  let addButton: JSX.Element = null;
  if (panelConfig.canAdd) {
    if (!panelConfig.canCopyFromShow || timetablesApi.data.data.length > 0) {
      addButton = <ButtonSecondary onClick={onNewRowClicked} label={RESX.Buttons.Add} />;
    } else {
      addButton = <ButtonSecondary onClick={onDisplayChooseOptionModal} label={RESX.Buttons.btnActivate} />;
    }
  }

  const { refToScrollTo } = useShortcutPanel({
    type: "timetable",
    label: panelConfig.headerLabel,
    totalItems: timetablesApi.data.data.length,
    onAddClicked: panelConfig.canAdd ? onNewRowClicked : undefined
  });

  return <>
    <Panel
      panelClassName="s1_related-feature-panel"
      panelRef={refToScrollTo}
      title={panelConfig.headerLabel}
      button={addButton}
      articleId={serverReference.supportArticleIds.whatIsATimetable}
      isLoading={timetablesApi.isLoading}
      hasError={timetablesApi.isError || timetablesApi.data.hasErrors}
      hasData={timetablesApi.data.data.length > 0}
    >
      <div className="list-with-gaps card-layout">
        <div className="list-with-gaps-body">
          {[...timetablesApi.data.data].sort(sortRows).map(event => (
            <TimetableRowCard
              noStartTimes={noStartTimes}
              noEndTimes={noEndTimes}
              key={event.timetableEntryId}
              row={event}
              canModify={panelConfig.canModify}
              onEditClicked={onEditRowClicked}
              onDeleteClicked={deleteTimetableRow}
            />
          ))}
        </div>
      </div>
    </Panel>

    <Modal
      title={editedRow ? RESX.Timetable.EditElement : RESX.Timetable.AddElement}
      width="small"
      isOpen={showEditModal.showModal}
      handleClose={onEditModalClose}
      gap="bigger"
    >
      <EditRow showId={showId} row={editedRow} onClose={onEditModalClose} />
    </Modal>

    <Modal
      title={RESX.Timetable.xTimetable}
      isOpen={displayChooseOptionModal}
      handleClose={() => { setDisplayChooseOptionModal(false); }}
    >
      <Choose
        onCreateNewRowSelected={() => {
          setDisplayChooseOptionModal(false);
          onNewRowClicked();
        }}
        onCopyFromShowSelected={() => {
          setDisplayChooseOptionModal(false);
          setDisplayLoadFromShowModal(true);
        }}
      />
    </Modal>

    <Modal
      title={RESX.Timetable.xTimetable}
      isOpen={displayLoadFromShowModal}
      handleClose={() => { setDisplayLoadFromShowModal(false); }}
      width="small"
      gap="larger"
      height={600}
    >
      <LoadFromShow
        defaultFilter={panelConfig.defaultFilters}
        initialInstruction={panelConfig.initialInstruction}
        showId={showId}
        onTimetableCopied={onTimetableCopied}
      />
    </Modal>

    <DeleteDialog
      isOpen={deleteRowModal.showDeleteDialog}
      handleClose={closeDeleteModal}
      onDelete={onDeleteConfirm}
      message={RESX.GeneralWarnings.DeleteSure}
    />
  </>;
};

export default TimetablePanel;